import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, InputDate, InputText, Table, Text } from "@pentabd/ui";
import { IconCalendar, IconSearch, IconXClose } from "@pentabd/icons";

import { ErrorModal, SuccessModal } from "@components";
import OwnTasksListTableColumn from "./OwnTasksListTableColumn";
import { useOwnTasksList } from "@hooks/taskManagement/ownTasks/useOwnTasksList";
import { OwnTasksResponseType } from "@type/taskManagement/ownTasks";
import { Pagination } from "@type/taskManagement/pagination";
import { StatusOptions } from "@type/taskManagement/common";
import { SelectControllerOptions } from "@components/SelectControllerOptions";
import { StatusResponseType } from "@type/taskManagement/settings/status";
import { useStatusList } from "@hooks/taskManagement/settings/status/useStatusList";

const OwnTasksListTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formMethods, setFormMethods] = useState<any>(null);

  const [statusOptions, setStatusOptions] = useState<StatusOptions>([
    { label: "All", value: "all" },
  ]);

  const { statusState } = useStatusList();
  const { getData: statusGetData } = statusState;
  const { data: statusData } = statusGetData || ({} as StatusResponseType);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const { handleQuery, ownTasksState, clearSearch, hasFilter, query } =
    useOwnTasksList();

  const { getData, deleteSuccess, deleteError } = ownTasksState;

  const { data, pagination } = getData || ({} as OwnTasksResponseType);

  const { page, total, size } = pagination || ({} as Pagination);

  const createSuccessAction = () => {
    setIsSuccessModalOpen(false);
    handleQuery("page", 1);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const handleStatus = (value: string) => {
    if (value !== "all") {
      handleQuery("status", value);
    } else {
      handleQuery("status", "");
    }
  };

  useEffect(() => {
    if (statusData) {
      const options = statusData.map((item: any) => ({
        label: item?.name || "",
        value: `${item?.id}` || "",
      }));

      setStatusOptions((prev) => [{ label: "All", value: "all" }, ...options]);
    }
  }, [statusGetData]);

  useEffect(() => {
    if (deleteSuccess) {
      setIsSuccessModalOpen(true);
    }

    if (deleteError) {
      setIsErrorModalOpen(true);
    }
  }, [deleteSuccess, deleteError]);

  return (
    <>
      <div className="mt-10">
        <Table
          columns={OwnTasksListTableColumn(t) as any}
          rows={data || []}
          headerExtension={{
            leftComponents: [
              <div className="row">
                <div className="col-12 col-lg-6 col-xl-4 m-0 pt-5 px-5">
                  <SelectControllerOptions
                    label={t("TM_OWN_TASKS.TASKS_LIST.TABLE.STATUS_FILTER")}
                    name={"status"}
                    options={statusOptions}
                    actionHandler={handleStatus}
                    defaultValue="all"
                    formMethods={(methods: any) => {
                      setFormMethods(methods);
                    }}
                  />
                </div>
                <div className="col-12 col-lg-6 col-xl-4 m-0 pt-5 px-5">
                  <Text
                    weight="semibold"
                    color="title"
                    size="sm"
                    component="p"
                    className="mb-3"
                  >
                    {t("TM_OWN_TASKS.TASKS_LIST.TABLE.START_DATE_FILTER")}
                  </Text>
                  <InputDate
                    name={"name"}
                    size="sm"
                    onSelectDate={(date) => {
                      handleQuery("startDate", date);
                    }}
                    defaultValue={query?.startDate as string}
                    placeholder={t(
                      "TM_OWN_TASKS.TASKS_LIST.TABLE.START_DATE_FILTER",
                    )}
                    prefix={<IconCalendar size="20" fill="subtitle2" />}
                    disabled={false}
                    portal={true}
                  />
                </div>
                <div className="col-12 col-lg-6 col-xl-4 m-0 pt-5 px-5">
                  <Text
                    weight="semibold"
                    color="title"
                    size="sm"
                    component="p"
                    className="mb-3"
                  >
                    {t("TM_OWN_TASKS.TASKS_LIST.TABLE.END_DATE_FILTER")}
                  </Text>
                  <InputDate
                    name={"name"}
                    size="sm"
                    onSelectDate={(date) => {
                      handleQuery("endDate", date);
                    }}
                    defaultValue={query?.endDate as string}
                    placeholder={t(
                      "TM_OWN_TASKS.TASKS_LIST.TABLE.END_DATE_FILTER",
                    )}
                    prefix={<IconCalendar size="20" fill="subtitle2" />}
                    disabled={false}
                    portal={true}
                  />
                </div>
                <div className="col-12 col-lg-6 col-xl-4 m-0 pt-5 px-5">
                  <Text
                    weight="semibold"
                    color="title"
                    size="sm"
                    component="p"
                    className="mb-3"
                  >
                    {t("TM_OWN_TASKS.TASKS_LIST.TABLE.SEARCH_LABEL")}
                  </Text>
                  <InputText
                    name="simple-input"
                    onChange={(e) => {
                      handleQuery("search", e.target.value);
                    }}
                    value={query?.search}
                    prefix={<IconSearch fill="dark" size="20" />}
                    size="sm"
                    placeholder={t("TM_OWN_TASKS.TASKS_LIST.TABLE.SEARCH")}
                    type="text"
                    controlling
                  />
                </div>
                <div className="col-12 col-lg-6 col-xl-4 m-0 pt-5 px-5">
                  <Text
                    weight="semibold"
                    color="title"
                    size="sm"
                    component="p"
                    className="mb-3"
                  >
                    {t("TM_OWN_TASKS.TASKS_LIST.TABLE.SEARCH_CLEAR")}
                  </Text>
                  <Button
                    fill="outline"
                    type="warning"
                    size="sm"
                    onClick={() => {
                      formMethods && formMethods.setValue("status", "all");
                      clearSearch();
                    }}
                    disabled={!hasFilter}
                  >
                    <IconXClose size="18" fill="warning" />
                  </Button>
                </div>
              </div>,
            ],
          }}
          pagination={{
            totalPage: Math.ceil(total / size),
            onClick: (currentPage) => {
              handleQuery("page", currentPage);
            },
            activePage: page,
          }}
        />
      </div>
      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
          subtitle={"You have deleted a own task"}
        />
      )}

      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}
    </>
  );
};

export default OwnTasksListTable;
